// Libs
import classNames from 'classnames/bind';
import { IconDiscussionLine, IconLinkLine, IconQuizLine, IconRefreshLine, IconGroupLine } from '@instructure/ui-icons';
import { useTranslation } from 'react-i18next';
import i18n from '~/thirdParties/i18n';
import { Badge, Button, Flex, Heading, IconButton, Spinner, Text, Tooltip, View } from '@instructure/ui';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
// Components, Layouts, Pages
import ModalExternalUrl from './component/ModalExternalUrl';
// Others
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getDetailCourse } from '~/thunks/course/courseThunk';
import Lirn from '../home/component/lirn/Lirn';
import {
  ENGLISH_VIDEO_LESSON_SUBTITLE,
  ITALIAN_VIDEO_LESSON_SUBTITLE,
  LAST_SECONDS_TO_MARK_WATCHED,
  MAX_VIDEO_TIME_FORWARD,
  PROGRESS_POST_INTERVAL_MS,
} from '~/utils/constants/common';
import { studentRoute } from '~/utils/routes/student';
import { MaterialTypeEnum } from '~/utils/enum/common';
import { WebSocketContext } from '~/context';
import { joinModule, leaveModule } from '~/utils/helper/websocket';
import { upsertModuleWatchProgress } from '~/thunks/module/moduleThunk';
import { moduleApi } from '~/apis/module';
// Styles, images, icons
import styles from './CourseDetail.module.scss';
import './CourseDetail.scss';
import { icons, images } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const formatPrerequisitesMessage = (prerequisites: any[], language: string): string => {
  if (!prerequisites || prerequisites.length === 0) return '';

  // Use "o" for Italian/Spanish, "or" for English and others
  const orConnector = language === 'it' || language === 'es' ? ' o ' : ' or ';

  const parts: string[] = [];

  prerequisites.forEach((prereq) => {
    const moduleName = prereq.moduleName;
    const requiredItems = prereq.requiredItems || [];

    // if (requiredItems.length === 0) {
    // Empty requiredItems means need to watch video
    parts.push(moduleName);
    // } else {
    //   // Has requiredItems means need to do quizzes
    //   const quizNames = requiredItems.join(', ');
    //   parts.push(`${moduleName}${orConnector}${quizNames}`);
    // }
  });

  return parts.join('; ');
};

const CourseDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/course', 'common/base']);
  const dispatch = useAppDispatch();
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef<ReactPlayer>(null);
  const { wss } = useContext(WebSocketContext);
  const hasSentWatchedRef = useRef(false);
  //#endregion Declare Hook

  //#region Selector
  const coursesDetail = useAppSelector((state) => state.courseState.coursesDetail);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [played, setPlayed] = useState(0);
  const [isExternalUrlModalOpen, setIsExternalUrlModalOpen] = useState(false);
  const [externalUrl, setExternalUrl] = useState<string | null>(null);
  const [externalUrlTitle, setExternalUrlTitle] = useState<string>('');
  const [isLocked, setIsLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState<string>('');
  const [prerequisites, setPrerequisites] = useState<any[]>([]);
  const isLessonVideo =
    coursesDetail.videoTitle?.includes(ENGLISH_VIDEO_LESSON_SUBTITLE) ||
    coursesDetail.videoTitle?.includes(ITALIAN_VIDEO_LESSON_SUBTITLE);
  //#endregion Declare State

  //#region Declare Memo
  const lastSentAtRef = useRef<number>(0);
  const lastSentSecondsRef = useRef<number>(0);

  const getCurrentSeconds = () => Math.floor(videoRef?.current?.getCurrentTime() ?? 0);
  const getDurationSeconds = () => Math.floor(videoRef?.current?.getDuration() ?? 0);

  const postProgressThunk = (lastWatchedSeconds: number, isWatched = false) => {
    if (!courseId || !moduleId || !isLessonVideo) return;
    dispatch(
      upsertModuleWatchProgress({
        moduleId: Number(moduleId),
        body: {
          courseId: Number(courseId),
          lastWatchedSeconds: Math.max(0, Math.floor(lastWatchedSeconds)),
          ...(isWatched ? { isWatched: true } : {}),
        },
      })
    );
  };

  const postProgressBeacon = (lastWatchedSeconds: number, isWatched = false) => {
    if (!courseId || !moduleId || !isLessonVideo) return;
    const success = moduleApi.upsertModuleWatchProgressBeacon(Number(moduleId), {
      courseId: Number(courseId),
      lastWatchedSeconds: Math.max(0, Math.floor(lastWatchedSeconds)),
      ...(isWatched ? { isWatched: true } : {}),
    });
    //if beacon queue failed -> fallback via thunk
    if (!success) {
      postProgressThunk(lastWatchedSeconds, isWatched);
    }
  };

  const maybeSendProgress = (seconds: number) => {
    const now = Date.now();
    if (now - (lastSentAtRef.current || 0) >= PROGRESS_POST_INTERVAL_MS && seconds !== lastSentSecondsRef.current) {
      lastSentAtRef.current = now;
      lastSentSecondsRef.current = seconds;
      postProgressThunk(seconds, false);
    }
  };
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDetailCourse();
  }, []);

  useEffect(() => {
    if (!courseId || !moduleId) {
      return;
    }

    if (wss) {
      joinModule(wss, +courseId, +moduleId);
    }

    return () => {
      if (wss) {
        leaveModule(wss, +courseId, +moduleId);
      }
    };
  }, [wss, courseId, moduleId]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) postProgressBeacon(getCurrentSeconds(), false);
    };
    const onBeforeUnload = () => {
      postProgressBeacon(getCurrentSeconds(), false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [courseId, moduleId]);

  // Update lockMessage when language changes
  useEffect(() => {
    if (prerequisites && prerequisites.length > 0) {
      const message = formatPrerequisitesMessage(prerequisites, i18n.language);
      setLockMessage(t('course_detail_lesson_locked', { message, ns: 'common/course' }));
    } else {
      setLockMessage('');
    }
  }, [i18n.language, prerequisites, t]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleProgress = (state: OnProgressProps) => {
    const current = videoRef?.current?.getCurrentTime() ?? 0;
    const duration = getDurationSeconds();
    const secondsLeft = duration - current;

    if (current <= played + MAX_VIDEO_TIME_FORWARD) {
      setPlayed(current);

      if (current > 0 && secondsLeft <= LAST_SECONDS_TO_MARK_WATCHED) {
        if (!hasSentWatchedRef.current) {
          postProgressThunk(duration, true);
          hasSentWatchedRef.current = true;
        }
        return;
      }

      maybeSendProgress(Math.floor(current));
      return;
    }

    videoRef?.current?.seekTo(played, 'seconds');
  };

  const handlePause = () => {
    postProgressThunk(getCurrentSeconds(), false);
  };

  const handleSeek = (seconds: number) => {
    postProgressThunk(Math.floor(seconds), false);
  };

  const handleEndedVideo = () => {
    const duration = getDurationSeconds();
    postProgressThunk(duration, true);
    setPlayed(0);
  };

  const handleGetDetailCourse = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    if (!courseId || !moduleId) return;

    try {
      const response = await dispatch(getDetailCourse({ coursesId: courseId, lessonId: moduleId })).unwrap();

      // Check if there are prerequisites
      if (response?.data?.prerequisites && response.data.prerequisites.length > 0) {
        setIsLocked(true);
        setPrerequisites(response.data.prerequisites);
        // lockMessage will be updated by useEffect when language changes
      } else {
        setIsLocked(false);
        setPrerequisites([]);
        setLockMessage('');
      }
      hasSentWatchedRef.current = false;
    } catch (e) {
      setErrorMessage((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderCourseContent = () => {
    const topics = coursesDetail?.discussionTopics ?? [];
    if (isLoading) {
      return (
        <div className={cx('loadingContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('buttonRetry')}>
          <Button onClick={handleGetDetailCourse} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('retry_button_label', { ns: 'common/base' })}
          </Button>
        </div>
      );
    }

    return (
      <>
        <div className={cx('leftSectionContent')}>
          <div className={cx('videoContainer')}>
            <ReactPlayer
              ref={videoRef}
              onProgress={handleProgress}
              onPause={handlePause}
              onSeek={handleSeek}
              onEnded={handleEndedVideo}
              width='100%'
              height='100%'
              className={cx('videoPlayer')}
              url={coursesDetail.videoUrl}
              controls
              autoPlay={true}
              playing={!isLocked}
              config={{
                file: {
                  attributes: {
                    poster: images.commonLogoFullPositiveThumbnail,
                    controlsList: 'nodownload noplaybackrate',
                    preload: 'metadata',
                  },
                },
              }}
            />
          </div>

          <div className={cx('courseInfo')}>
            <Heading level='h3'>{coursesDetail?.moduleName}</Heading>
            <Text variant='content'>
              {t('course_detail_teacher_name')} {coursesDetail?.teacherName}
              {coursesDetail?.teacherEmail ? (
                <>
                  {' - '}
                  <a href={`mailto:${coursesDetail.teacherEmail}`} className={cx('teacherEmailLink')}>
                    {t('course_detail_send_email')}
                  </a>
                </>
              ) : null}
            </Text>
            <Text variant='content'>{coursesDetail?.courseName}</Text>

            {topics.length > 0 && (
              <div className={cx('badgeContainer')}>
                <View className={'roundIcon'}>
                  <Flex gap='small'>
                    {topics.map((topic: any) => (
                      <Badge key={topic.id} count={topic.unreadCount ?? 0} placement='top end'>
                        <Tooltip
                          renderTip={
                            topic.accessType === 'student_only'
                              ? t('course_detail_join_student_discussion_tooltip')
                              : t('course_detail_join_discussion_tooltip')
                          }
                        >
                          <IconButton
                            renderIcon={topic.accessType === 'student_only' ? IconGroupLine : IconDiscussionLine}
                            screenReaderLabel='Discussion page'
                            withBorder={false}
                            withBackground={false}
                            onClick={() => handleNavigateDiscussionPage(String(topic.id))}
                          />
                        </Tooltip>
                      </Badge>
                    ))}
                  </Flex>
                </View>
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  const renderMaterialContent = () => {
    if (isLoading) {
      return (
        <div className={cx('loadingContainer')}>
          <Spinner size='small' />
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className={cx('buttonRetry')}>
          <Button onClick={handleGetDetailCourse} size='medium' color='primary' renderIcon={<IconRefreshLine />}>
            {t('retry_button_label', { ns: 'common/base' })}
          </Button>
        </div>
      );
    }

    if (!coursesDetail?.materials || coursesDetail?.materials?.length === 0) {
      return (
        <div className={cx('noDataText')}>
          <Text variant='contentSmall'>{t('course_detail_no_material_link_available')}</Text>
        </div>
      );
    }

    return (
      <>
        <Flex display='flex' direction='row' alignItems='center' justifyItems='center' width={'100%'}>
          <Heading level='h3'>{t('course_detail_material_title')}</Heading>
        </Flex>

        <div className={cx('materialContainer')}>
          {coursesDetail.materials.map((material, index) => {
            const isQuiz = material.type === MaterialTypeEnum.QUIZ;
            const isExternalUrl =
              material.type === MaterialTypeEnum.EXTERNAL_URL &&
              material.link &&
              (material.link.toLowerCase().endsWith('.mp4') ||
                material.link.toLowerCase().endsWith('.webm') ||
                material.link.toLowerCase().endsWith('.ogg') ||
                material.link.toLowerCase().endsWith('.mov') ||
                material.link.toLowerCase().endsWith('.avi') ||
                material.link.toLowerCase().endsWith('.mkv') ||
                ReactPlayer.canPlay(material.link));
            const materialLink = isQuiz
              ? `${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.quiz}/${material.id}`
              : material.link;

            const handleMaterialClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
              if (isExternalUrl && material.link) {
                e.preventDefault();
                setExternalUrl(material.link);
                setExternalUrlTitle(material.title);
                setIsExternalUrlModalOpen(true);
              }
            };

            return (
              <div key={index} className={cx('materialItem')}>
                <div className={cx('itemRow')}>
                  {material.type === MaterialTypeEnum.QUIZ ? <IconQuizLine /> : <IconLinkLine />}

                  <a
                    href={materialLink}
                    className={cx('itemLink')}
                    onClick={handleMaterialClick}
                    {...(!isQuiz && !isExternalUrl && { target: '_blank', rel: 'noopener noreferrer' })}
                  >
                    <Text variant='content'>{material.title}</Text>
                  </a>
                </div>
                <hr className={cx('line')} />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const handleCloseExternalUrlModal = () => {
    setIsExternalUrlModalOpen(false);
    setExternalUrl(null);
    setExternalUrlTitle('');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleNavigateDiscussionPage = (discussionId: string) => {
    navigate(`${studentRoute.base}${studentRoute.course}/${courseId}${studentRoute.discussion}/${discussionId}`);
  };
  //#endregion Handle Function

  return (
    <div className={cx('container')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('course_detail_back')}</Text>
      </div>

      <div className={cx('contentWrapper')}>
        {isLocked && (
          <div className={cx('lockedOverlay')}>
            <div className={cx('lockedMessage')}>
              <Text size='large' weight='bold'>
                {lockMessage}
              </Text>
            </div>
          </div>
        )}

        <div className={cx('content', { locked: isLocked })}>
          <div className={cx('leftSection')}>{renderCourseContent()}</div>

          <div className={cx('rightSection')}>
            <div className={cx('rightSectionItem')}>{renderMaterialContent()}</div>

            <div className={cx('rightSectionItem', 'two')}>{<Lirn />}</div>
          </div>
        </div>
      </div>

      <ModalExternalUrl
        isModalOpen={isExternalUrlModalOpen}
        externalUrl={externalUrl}
        title={externalUrlTitle}
        courseId={courseId}
        moduleId={moduleId}
        onClose={handleCloseExternalUrlModal}
      />
    </div>
  );
};

export default CourseDetail;
